import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMap } from './useMap';
import styles from './LocationMarker.module.css';

/**
 * Stable string hash → unsigned 32-bit integer. JS-canonical
 * (same as Java's `String.hashCode` modulo sign). Deterministic
 * so a given location ID always lands on the same pulse phase
 * across reloads — animations stay consistent for the operator
 * rather than reshuffling each page open.
 */
function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // force int32
  }
  return Math.abs(h);
}

interface BaseLocation {
  _id: string;
  name: string;
  chargerCount: number;
  onlineChargerCount: number;
  lng: number;
  lat: number;
}

interface LocationMarkerProps {
  location: BaseLocation;
  selected: boolean;
  offset?: [number, number];
  onSelect: (location: BaseLocation) => void;
}

/**
 * Single pulsing location dot. Vanilla DOM marker mounted via
 * `new mapboxgl.Marker({ element })` — Mapbox doesn't ship a
 * React component, and going through a portal would defeat the
 * performance argument behind raw DOM.
 *
 * Per-marker stagger is the headline visual: each marker's
 * pulse phase is offset by 0–2.5s based on a deterministic
 * hash of its `_id`. Same map, same locations, same animations
 * across reloads — but distinct from neighbor markers so the
 * map breathes rather than flashing.
 */
export function LocationMarker({ location, selected, onSelect, offset }: LocationMarkerProps) {
  const { map, isLoaded } = useMap();
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);
  const current = useRef({ location, onSelect });
  current.current = { location, onSelect };

  // Mount the marker once per (map, location._id) pair. The
  // location's lng/lat can update without remount (we
  // re-setLngLat in the second effect below).
  useEffect(() => {
    if (!map || !isLoaded) return;

    const el = document.createElement('div');
    el.className = `${styles.wrapper} location-marker-wrapper`; // keep stable global class for click-outside detection in info card
    elRef.current = el;

    // Stagger: 0–2.5s deterministic offset per marker.
    const stagger = (hashCode(location._id) % 1000) / 1000 * 2.5;
    el.style.setProperty('--marker-pulse-delay', `${stagger}s`);

    // Inner DOM mirrors the eviinfra PropertyMarker structure
    // (two rings + glow + dot + tooltip).
    el.innerHTML = `
      <div class="${styles.ringOuter}"></div>
      <div class="${styles.ringMiddle}"></div>
      <div class="${styles.dotContainer}">
        <div class="${styles.glow}"></div>
        <div class="${styles.dot}"></div>
      </div>
      <div class="${styles.tooltip}">
        <span class="${styles.tooltipName}"></span>
        <span class="${styles.tooltipMeta}"></span>
      </div>
    `;

    // Set tooltip content via textContent to avoid HTML injection
    // through location names (operator-controlled but defensive).
    const tipName = el.querySelector(`.${styles.tooltipName}`);
    const tipMeta = el.querySelector(`.${styles.tooltipMeta}`);
    if (tipName) tipName.textContent = location.name;
    if (tipMeta) {
      tipMeta.textContent =
        `${location.onlineChargerCount}/${location.chargerCount} online`;
    }

    const handleClick = (e: Event) => {
      e.stopPropagation();
      current.current.onSelect(current.current.location);
    };
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    el.setAttribute('aria-label', location.name);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); }
    };
    el.addEventListener('keydown', handleKey);
    el.addEventListener('click', handleClick);

    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([location.lng, location.lat])
      .addTo(map);
    markerRef.current = marker;

    return () => {
      el.removeEventListener('keydown', handleKey);
      el.removeEventListener('click', handleClick);
      marker.remove();
      markerRef.current = null;
      elRef.current = null;
    };
    // We intentionally re-mount when the location identity changes;
    // dependency on the primitive id avoids needless rebuilds on
    // unrelated parent re-renders. `onSelect` is captured by closure
    // and intentionally not in deps to avoid full remount when the
    // parent's handler identity changes (it's stable in practice;
    // a stale closure on selection would just call the prior
    // handler, which still reads from the latest selected state).
  }, [map, isLoaded, location._id]);

  // Cheap re-setLngLat path when only coordinates change.
  useEffect(() => {
    if (!markerRef.current) return;
    markerRef.current.setLngLat([location.lng, location.lat]);
  }, [location.lng, location.lat]);

  const offsetX = offset?.[0] ?? 0;
  const offsetY = offset?.[1] ?? 0;
  useEffect(() => { markerRef.current?.setOffset([offsetX, offsetY]); }, [map, isLoaded, offsetX, offsetY]);

  // Toggle the .wrapperSelected class on the marker element — adds
  // the brighter dot + suppresses the rings + hides the tooltip so
  // the open info-card has the selected marker as a stable anchor.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    // CSS-module class lookups under `noUncheckedIndexedAccess`
    // type as `string | undefined`; fall through if unset (the
    // `.wrapperSelected` class is declared in our own module so
    // this is a typesystem appeasement rather than a real branch).
    const selectedClass = styles.wrapperSelected;
    if (!selectedClass) return;
    if (selected) {
      el.classList.add(selectedClass);
    } else {
      el.classList.remove(selectedClass);
    }
  }, [selected]);

  // Update tooltip meta when counts shift (e.g. a charger goes
  // offline mid-session and the 60s map-points poll surfaces it).
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const tipMeta = el.querySelector(`.${styles.tooltipMeta}`);
    if (tipMeta) {
      tipMeta.textContent =
        `${location.onlineChargerCount}/${location.chargerCount} online`;
    }
  }, [location.chargerCount, location.onlineChargerCount]);

  return null; // marker is mounted imperatively into mapbox-gl
}

/**
 * Static cluster dot — count label centered, no pulse. Same
 * vanilla-DOM mounting as LocationMarker; visually distinct via
 * the `.wrapperCluster` modifier in the shared CSS module.
 *
 * Click handler expands the cluster (delegates to the parent's
 * zoom-on-cluster-click handler), matching the supercluster
 * default affordance.
 */
interface ClusterMarkerProps {
  clusterId: number;
  lng: number;
  lat: number;
  chargerSum: number;
  onClick: (clusterId: number, lng: number, lat: number) => void;
}

export function ClusterMarker({ clusterId, lng, lat, chargerSum, onClick }: ClusterMarkerProps) {
  const { map, isLoaded } = useMap();
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;

    const el = document.createElement('div');
    el.className = `${styles.wrapper} ${styles.wrapperCluster} location-marker-wrapper`;

    el.innerHTML = `
      <div class="${styles.dot}">${chargerSum}</div>
    `;

    const handleClick = (e: Event) => {
      e.stopPropagation();
      onClick(clusterId, lng, lat);
    };
    el.addEventListener('click', handleClick);

    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(map);
    markerRef.current = marker;

    return () => {
      el.removeEventListener('click', handleClick);
      marker.remove();
      markerRef.current = null;
    };
    // `onClick` intentionally excluded — same closure-capture pattern
    // as LocationMarker. Recreating the marker on handler identity
    // churn would thrash the map.
  }, [map, isLoaded, clusterId, lng, lat, chargerSum]);

  return null;
}
